import { useQuery } from '@tanstack/react-query';
import {
  addDays,
  eatDayOf,
  eatTimeOf,
  formatIsoDay,
  parseIsoDay,
  startOfEatDay,
} from '@wodi/domain';
import type { OpeningStockImportParameters, PriceListImportParameters } from '@wodi/shared';
import {
  Checkbox,
  DateField,
  LoadingBlock,
  Notice,
  RadioGroup,
  Select,
  TextArea,
  TextField,
} from '@wodi/ui';

import { priceListsQuery } from '../api/finance.ts';
import { storesQuery } from '../api/stock.ts';
import styles from '../patients/patients.module.css';

// The parameter panels of Settings > Imports > New import for price lists and opening
// stock (docs/plans/phase1-importers.md §8). Each draft is plain state; `…Problems` says
// what is missing in words, and `…Parameters` builds what the API takes.

// --- Price lists ----------------------------------------------------------------------

export interface PriceListDraft {
  priceListId: string | null;
  startsWhen: 'now' | 'day';
  startsOn: string | null;
  createMissingServices: boolean;
}

export const emptyPriceListDraft = (priceListId: string | null = null): PriceListDraft => ({
  priceListId,
  startsWhen: 'now',
  startsOn: null,
  createMissingServices: false,
});

/** The first day a price can start on: tomorrow, because today has already begun. */
export function firstStartDay(now: Date): string {
  return formatIsoDay(addDays(eatDayOf(now), 1));
}

export function priceListProblems(draft: PriceListDraft, now: Date) {
  let startsOn: string | undefined;
  if (draft.startsWhen === 'day') {
    if (draft.startsOn === null) startsOn = 'Enter the day the prices start.';
    else if (draft.startsOn < firstStartDay(now))
      startsOn = 'Prices start after today, so charges already made keep their price.';
  }
  return {
    priceListId: draft.priceListId === null ? 'Choose the price list.' : undefined,
    startsOn,
  };
}

export function priceListParameters(draft: PriceListDraft): PriceListImportParameters {
  if (draft.priceListId === null) throw new Error('form incomplete');
  return {
    priceListId: draft.priceListId,
    startsOn: draft.startsWhen === 'now' || draft.startsOn === null ? 'now' : draft.startsOn,
    createMissingServices: draft.createMissingServices,
  };
}

export function PriceListFields({
  draft,
  onChange,
  showProblems,
  canCreateServices,
}: {
  draft: PriceListDraft;
  onChange: (draft: PriceListDraft) => void;
  showProblems: boolean;
  canCreateServices: boolean;
}) {
  const lists = useQuery(priceListsQuery);
  const now = new Date();
  const problems = priceListProblems(draft, now);
  if (lists.isPending) return <LoadingBlock label="Loading price lists" rows={1} />;
  if (lists.isError)
    return (
      <Notice tone="error">Price lists couldn't be loaded. Reload the page to try again.</Notice>
    );
  const active = lists.data.filter((l) => l.status === 'active');
  return (
    <>
      <p>
        Prices are scheduled the same way as Change price: an old price is kept in the history and
        ends where the new one starts. A blank price is a problem, never zero; write 0 for a free
        item.
      </p>
      <Select
        label="Price list"
        placeholder="Choose a price list"
        options={active.map((l) => ({ id: l.id, label: l.name }))}
        value={draft.priceListId}
        onChange={(value) => {
          onChange({ ...draft, priceListId: String(value) });
        }}
        {...(showProblems && problems.priceListId !== undefined
          ? { errorMessage: problems.priceListId }
          : {})}
      />
      <RadioGroup
        label="Prices start"
        value={draft.startsWhen}
        options={[
          { id: 'now', label: 'Now' },
          { id: 'day', label: 'On a later day' },
        ]}
        onChange={(value) => {
          onChange({ ...draft, startsWhen: value === 'day' ? 'day' : 'now' });
        }}
      />
      {draft.startsWhen === 'day' ? (
        <DateField
          label="Start day"
          description="At 00:00 that day. A row's own starts_on overrides it."
          value={draft.startsOn}
          minValue={firstStartDay(now)}
          onChange={(value) => {
            onChange({ ...draft, startsOn: value });
          }}
          {...(showProblems && problems.startsOn !== undefined
            ? { errorMessage: problems.startsOn }
            : {})}
        />
      ) : null}
      {canCreateServices ? (
        <Checkbox
          isSelected={draft.createMissingServices}
          onChange={(value) => {
            onChange({ ...draft, createMissingServices: value });
          }}
          description="Rows need name, category, kind and billing_unit. Stock items are never created here."
        >
          Create services that aren't in the catalogue
        </Checkbox>
      ) : null}
    </>
  );
}

// --- Opening stock --------------------------------------------------------------------

export interface OpeningStockDraft {
  storeId: string | null;
  countedOn: string | null;
  /** HH:MM, East Africa Time. */
  countedTime: string;
  note: string;
}

const pad = (value: number) => String(value).padStart(2, '0');

export function emptyOpeningStockDraft(storeId: string | null, now: Date): OpeningStockDraft {
  const time = eatTimeOf(now);
  return {
    storeId,
    countedOn: formatIsoDay(eatDayOf(now)),
    countedTime: `${pad(time.hour)}:${pad(time.minute)}`,
    note: '',
  };
}

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/** The instant of the count, or null when the day or time can't be read. */
export function countedAt(draft: OpeningStockDraft): Date | null {
  if (draft.countedOn === null || !TIME_PATTERN.test(draft.countedTime)) return null;
  const day = parseIsoDay(draft.countedOn);
  if (!day.ok) return null;
  const [hour, minute] = draft.countedTime.split(':').map(Number);
  return new Date(startOfEatDay(day.value).getTime() + ((hour ?? 0) * 60 + (minute ?? 0)) * 60_000);
}

export function openingStockProblems(draft: OpeningStockDraft, now: Date) {
  const at = countedAt(draft);
  return {
    storeId: draft.storeId === null ? 'Choose the store that was counted.' : undefined,
    countedOn: draft.countedOn === null ? 'Enter the day the stock was counted.' : undefined,
    countedTime: !TIME_PATTERN.test(draft.countedTime)
      ? 'Write the time as HH:MM, such as 08:30.'
      : at !== null && at.getTime() > now.getTime()
        ? 'The count time is in the future. Enter when the stock was counted.'
        : undefined,
    note: draft.note.trim().length > 400 ? 'Keep the note under 400 characters.' : undefined,
  };
}

export function openingStockParameters(draft: OpeningStockDraft): OpeningStockImportParameters {
  const at = countedAt(draft);
  if (draft.storeId === null || at === null) throw new Error('form incomplete');
  const note = draft.note.trim();
  return { storeId: draft.storeId, countedAt: at.toISOString(), note: note === '' ? null : note };
}

export function OpeningStockFields({
  draft,
  onChange,
  showProblems,
}: {
  draft: OpeningStockDraft;
  onChange: (draft: OpeningStockDraft) => void;
  showProblems: boolean;
}) {
  const stores = useQuery(storesQuery);
  const now = new Date();
  const problems = openingStockProblems(draft, now);
  const error = (key: keyof typeof problems) =>
    showProblems && problems[key] !== undefined ? { errorMessage: problems[key] } : {};
  if (stores.isPending) return <LoadingBlock label="Loading stores" rows={1} />;
  if (stores.isError)
    return <Notice tone="error">Stores couldn't be loaded. Reload the page to try again.</Notice>;
  const active = stores.data.filter((s) => s.status === 'active');
  return (
    <>
      <p>
        The import asks for opening balances; it doesn't change stock. Someone else with approval
        rights approves each document in the store's adjustments before the stock can be used.
      </p>
      <Select
        label="Store"
        placeholder="Choose a store"
        options={active.map((s) => ({ id: s.id, label: s.name }))}
        value={draft.storeId}
        onChange={(value) => {
          onChange({ ...draft, storeId: String(value) });
        }}
        {...error('storeId')}
      />
      <div className={styles.row}>
        <DateField
          label="Day counted"
          value={draft.countedOn}
          maxValue={formatIsoDay(eatDayOf(now))}
          onChange={(value) => {
            onChange({ ...draft, countedOn: value });
          }}
          {...error('countedOn')}
        />
        <TextField
          label="Time counted"
          description="HH:MM"
          value={draft.countedTime}
          onChange={(value) => {
            onChange({ ...draft, countedTime: value.trim() });
          }}
          {...error('countedTime')}
        />
      </div>
      <TextArea
        label="Note (optional)"
        description="Kept on every document, such as who counted."
        value={draft.note}
        onChange={(value) => {
          onChange({ ...draft, note: value });
        }}
        {...error('note')}
      />
    </>
  );
}
